// Team management API: reads and roster changes go over auto-REST (the backend
// gates them with membership rules + role hooks); team creation and the invite
// user-lookup are custom routes (atomic team+scope provisioning, and users are
// not listable by normal accounts).

import { pb, authHeader } from "./client.js";
import { sendJSON } from "./registry.js";
import type { ScopeRecord, TeamRecord, TeamMemberRecord } from "./registry.js";
import { roleAtLeast, type Role } from "../lib/roles.js";

const BASE = "/api/registry";

/** myTeams returns the current user's memberships with the team expanded. */
export function myTeams(): Promise<TeamMemberRecord[]> {
  const userId = pb.authStore.record?.id ?? "";
  return pb.collection("team_members").getFullList<TeamMemberRecord>({
    filter: pb.filter("user = {:u}", { u: userId }),
    expand: "team",
    sort: "created",
  });
}

export function getTeamBySlug(slug: string): Promise<TeamRecord> {
  return pb
    .collection("teams")
    .getFirstListItem<TeamRecord>(pb.filter("name = {:n}", { n: slug }));
}

/** listMembers returns a team's roster (visible to team members only). */
export function listMembers(teamId: string): Promise<TeamMemberRecord[]> {
  return pb.collection("team_members").getFullList<TeamMemberRecord>({
    filter: pb.filter("team = {:t}", { t: teamId }),
    expand: "user",
    sort: "created",
  });
}

/** createTeam claims the team name + same-named scope; the creator becomes owner. */
export function createTeam(
  name: string,
  displayName: string,
): Promise<{ id: string; name: string; display_name: string }> {
  return sendJSON(`${BASE}/teams`, {
    method: "POST",
    headers: { "Content-Type": "application/json", ...authHeader() },
    body: JSON.stringify({ name, display_name: displayName }),
  });
}

export interface LookupResult {
  id: string;
  handle: string;
  name: string;
}

/** lookupUser finds an account by exact handle or email (for invites). */
export function lookupUser(q: string): Promise<LookupResult> {
  return sendJSON<LookupResult>(`${BASE}/users/lookup?q=${encodeURIComponent(q)}`, {
    method: "GET",
    headers: authHeader(),
  });
}

/** invite adds the user matching q (handle or email) to the team with role. */
export async function invite(teamId: string, q: string, role: Role): Promise<LookupResult> {
  const user = await lookupUser(q.trim());
  await pb.collection("team_members").create({ user: user.id, team: teamId, role });
  return user;
}

export function updateRole(memberId: string, role: Role): Promise<TeamMemberRecord> {
  return pb.collection("team_members").update<TeamMemberRecord>(memberId, { role });
}

export function removeMember(memberId: string): Promise<boolean> {
  return pb.collection("team_members").delete(memberId);
}

export function deleteTeam(teamId: string): Promise<boolean> {
  return pb.collection("teams").delete(teamId);
}

/** myRoleIn picks the current user's role out of a loaded roster. */
export function myRoleIn(members: TeamMemberRecord[]): Role | null {
  const me = pb.authStore.record?.id;
  if (!me) return null;
  return members.find((m) => m.user === me)?.role ?? null;
}

/** myRoleInTeam looks up the current user's role in a team, or null when not a member. */
export async function myRoleInTeam(teamId: string): Promise<Role | null> {
  const me = pb.authStore.record?.id;
  if (!me) return null;
  try {
    const rec = await pb
      .collection("team_members")
      .getFirstListItem<TeamMemberRecord>(
        pb.filter("user = {:u} && team = {:t}", { u: me, t: teamId }),
      );
    return rec.role;
  } catch (e) {
    if ((e as { status?: number } | null)?.status === 404) return null;
    throw e;
  }
}

/** myWritableScopes returns the scopes the current user can publish into (member+). */
export async function myWritableScopes(): Promise<ScopeRecord[]> {
  const memberships = await myTeams();
  const teamIds = [
    ...new Set(memberships.filter((m) => roleAtLeast(m.role, "member")).map((m) => m.team)),
  ];
  if (teamIds.length === 0) return [];
  const params: Record<string, string> = {};
  const filter = teamIds
    .map((id, i) => {
      params[`t${i}`] = id;
      return `team = {:t${i}}`;
    })
    .join(" || ");
  return pb.collection("scopes").getFullList<ScopeRecord>({
    filter: pb.filter(filter, params),
    sort: "name",
  });
}
